import { ModalConfirmation, ModalConfirmationProps, notify, useModal } from '@billy/design-system'
import { ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { useVatDeclaration } from '@features/vatDeclarations/hooks/useVatDeclaration'

import { queryClient } from '../../../../config/queryClient'
import { NotificationKeys } from '../../../../enums/notificationKeys'
import { QueryKeys } from '../../../../enums/queryKeys'
import { useSegment } from '../../../../hooks'
import { getPeriodData } from '../../utils/getPeriodData'
import { useVatDeclarationPayments } from './hooks/useVatDeclarationPayments'
import { useVoidVatDeclarationPayment } from './hooks/useVoidVatDeclarationPayment'

interface VatDeclarationsVoidPeriodPaymentConfirmationModalProps extends ModalConfirmationProps {
  vatDeclarationId: string
}

export const VatDeclarationsVoidPeriodPaymentConfirmationModal = ({
  vatDeclarationId,
  id,
  ...rest
}: VatDeclarationsVoidPeriodPaymentConfirmationModalProps): ReactElement => {
  const { t } = useTranslation()
  const { track } = useSegment()
  const { close: closeModal } = useModal(id)
  const { vatDeclarationPayments } = useVatDeclarationPayments(vatDeclarationId)
  const { vatDeclaration } = useVatDeclaration({ vatDeclarationId })

  const periodData = getPeriodData(vatDeclaration?.period || '')
  const currentCorrection = vatDeclaration?.corrections?.find(
    ({ salesTaxReturnId }) => salesTaxReturnId === vatDeclaration?.id,
  )

  const { voidPeriodPayment, isProcessing } = useVoidVatDeclarationPayment({
    onError: (error) => {
      closeModal()
      notify({
        id: NotificationKeys.VatDeclarationsVoidPayment,
        message: error?.body?.errors[0]?.detail || t('vat_declarations.void_payment_modal.fail'),
        variant: 'error',
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries(QueryKeys.VatDeclarations)
      queryClient.invalidateQueries(QueryKeys.TaxReturnPeriods)
      closeModal()
      notify({
        id: NotificationKeys.VatDeclarationsVoidPayment,
        message: t('vat_declarations.void_payment_modal.success'),
        variant: 'success',
      })

      if (periodData && currentCorrection) {
        track('VAT Declaration Payment Voided (Client)', {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          period_settled: periodData.periodSettled,
          year: periodData.year,
          context: 'direct',
          type: currentCorrection.correctionNo > 0 ? 'correction' : 'normal',
        })
      }
    },
  })

  const handleVoidPaymentClick = useCallback(() => {
    if (vatDeclarationPayments && vatDeclarationPayments.length === 1) {
      voidPeriodPayment({ vatDeclarationId: vatDeclarationPayments[0].id })
    } else {
      closeModal()

      notify({
        id: NotificationKeys.VatDeclarationsVoidPayment,
        message: t('vat_declarations.void_payment_modal.fail.missing_id'),
        variant: 'error',
      })
    }
  }, [vatDeclarationPayments, voidPeriodPayment, closeModal, t])

  return (
    <ModalConfirmation
      {...rest}
      id={id}
      cancelLabel={t('vat_declarations.void_payment_modal.cancel')}
      danger
      message={t('vat_declarations.void_payment_modal.message')}
      okLabel={t('vat_declarations.void_payment_modal.approve')}
      okLoading={isProcessing}
      onOk={handleVoidPaymentClick}
      title={t('vat_declarations.void_payment_modal.title')}
    />
  )
}
